import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { toDate, formatDate } from '../../../../utils/dateHelpers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import {
    setActiveStep,
    setConsentSuccessful,
    setDigitalSignatureForm,
    setOpenSignConsentTwoSpanish,
    validateConsentTwo,
} from '../../../../store/consentSlice.slice';
import { getConsentFormDocumentByMember } from '../../../../store/assessment.slice';
import Signature from '../../Shared/Signature';
import ToggleLanguage from '../ToggleLanguage';

const ConsentPartTwoSpanish = () => {
    const dispatch = useAppDispatch();
    const { activeStep, consentSuccessful, digitalSignatureForm, isSaving, openSignConsentTwoSpanish } = useSelector((state: RootState) => state.consentSlice);
    const { memberBasicInfo } = useSelector((state: RootState) => state.memberDetail);
    const [openMemberSignatureOne, setOpenMemberSignatureOne] = useState(false);
    const [openGuardian, setOpenGuardian] = useState(false);
    const [openDecisionMaker, setOpenDecisionMaker] = useState(false);
    const [openVerbalConsent, setOpenVerbalConsent] = useState(false);

    const handleSubmitConsentPartTwo = () => {
        dispatch(validateConsentTwo(digitalSignatureForm, false));
    };

    useEffect(() => {
        if (consentSuccessful) {
            dispatch(setConsentSuccessful(false));
            dispatch(getConsentFormDocumentByMember(memberBasicInfo.Id));
            dispatch(setOpenSignConsentTwoSpanish(false));
        }
    }, [consentSuccessful, memberBasicInfo.Id, dispatch]);

    return (
        <Dialog open={openSignConsentTwoSpanish} onClose={() => dispatch(setOpenSignConsentTwoSpanish(false))} maxWidth="lg" fullWidth>
            <DialogTitle>
                <ToggleLanguage />
                <Stack spacing={1} my={5} textAlign={'center'}>
                    <Typography variant="h5" fontWeight={'bold'} mt={5}>
                        CONSENTIMIENTO PARA DIVULGAR INFORMACIÓN SOBRE SALUD MENTAL Y DROGADICCIÓN
                    </Typography>
                    <Typography variant="h6">(PARA PROVEEDORES TRATANTES)</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Box p={2} textAlign={'left'} pb={5}>
                    <Grid container spacing={2} mb={5}>
                        <Grid size={8}>
                            <TextField
                                fullWidth
                                variant="standard"
                                label="Nombre del paciente"
                                id="member-name"
                                className="initials-div"
                                value={digitalSignatureForm.MemberName}
                                error={digitalSignatureForm.MemberName.length === 0}
                                onChange={(e) =>
                                    dispatch(
                                        setDigitalSignatureForm({
                                            ...digitalSignatureForm,
                                            MemberName: e.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField
                                fullWidth
                                variant="standard"
                                label="Fecha de nacimiento"
                                value={(() => {
                                    const d = toDate(memberBasicInfo?.DOB);
                                    return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                })()}
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="body1">
                        Al firmar este formulario, permito que todos mis proveedores de servicios médicos anteriores, actuales y futuros en donde haya recibido
                        tratamiento de salud mental, incluido cualquier tratamiento para trastornos por consumo de sustancias, divulguen mi información a Health
                        Current, el intercambio de información de salud (HIE, por su sigla en inglés) estatal y a la organización que se indica aquí:
                    </Typography>
                    <Grid container spacing={2} my={2}>
                        <Grid size={8}>
                            <TextField
                                fullWidth
                                variant="standard"
                                label="Nombre de la organización de servicios médicos relacionada con el tratamiento"
                                value={'Catalytic Health Partners, Inc'}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField fullWidth variant="standard" label="Número de teléfono" value={'602-802-8388'} />
                        </Grid>
                        <Grid size={12}>
                            <TextField fullWidth variant="standard" label="Dirección" value={'3050 W Agua Fria Fwy, Suite 150'} />
                        </Grid>
                        <Grid size={4}>
                            <TextField fullWidth variant="standard" label="Ciudad" value={'Phoenix'} />
                        </Grid>
                        <Grid size={4}>
                            <TextField fullWidth variant="standard" label="Estado" value={'AZ'} />
                        </Grid>
                        <Grid size={4}>
                            <TextField fullWidth variant="standard" label="Código postal" value={'85027'} />
                        </Grid>
                    </Grid>
                    <Typography variant="body1" pt={2}>
                        Recibo (o recibiré) tratamiento de esta organización. El propósito de esta divulgación es para:
                    </Typography>

                    <List sx={{ listStyleType: 'disc', px: 2 }}>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">Mi tratamiento;</Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">El pago de mi tratamiento (por ejemplo, empresas de facturación de seguros); y</Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Actividades de servicios médicos (por ejemplo, mejorar la calidad de atención para los pacientes, organizar la atención de los
                                pacientes, actividades para la seguridad de los pacientes y otras actividades necesarias para dirigir una organización de
                                servicios médicos).
                            </Typography>
                        </ListItem>
                    </List>
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            Autorizo la divulgación de toda mi información médica para estos propósitos, incluida la información de salud mental y la
                            información sobre trastornos por consumo de sustancias (p. ej., tratamiento para la drogadicción y el alcoholismo), mi historia
                            clínica, el diagnóstico, los registros hospitalarios, la información sobre consultas médicas y clínicas, los medicamentos, las
                            alergias, los resultados de laboratorio, los informes radiológicos, la salud sexual y reproductiva, la información relacionada con
                            enfermedades contagiosas y la información relacionada con el VIH/SIDA.
                        </Typography>
                        <Typography variant="body1">
                            Comprendo que la organización que se menciona anteriormente obtendrá esta información sobre mí a través de Health Current, el HIE
                            estatal. Comprendo que si anteriormente opté por que no se comparta mi información médica a través del HIE, este formulario cambiará
                            esa decisión. Comprendo que si firmo este formulario, acepto que se comparta mi información médica a través del HIE. Comprendo que
                            puedo cambiar esta decisión en cualquier momento.
                        </Typography>
                        <Typography variant="body1">
                            Comprendo que puedo retirar o cancelar este consentimiento para compartir mi información en cualquier momento, excepto cuando
                            alguien ya se haya basado en mi consentimiento para divulgar la información. Si deseo cancelar mi consentimiento o si tengo
                            preguntas, me comunicaré con la organización a través de la información de contacto que se menciona anteriormente.{' '}
                            <b>
                                A menos que cancele este consentimiento antes, este finalizará automáticamente en el transcurso de <u>un año</u> a partir de la
                                fecha de mi firma.
                            </b>{' '}
                            Comprendo que mi información sobre el tratamiento para trastornos por consumo de sustancias seguirá estando protegida por la ley
                            federal después de que se divulgue.
                        </Typography>
                    </Stack>
                    <div id="consent-two-signature" className="initials-div"></div>

                    <Stack justifyContent={'space-between'} direction={'row'} alignItems={'flex-end'} my={5}>
                        <Typography variant="body1" color={'error'}>
                            * Por favor firme uno a continuación
                        </Typography>
                        <Stack direction={'row'} alignItems={'flex-end'} spacing={1}>
                            <Typography variant="h6">Fecha:</Typography>
                            <Typography variant="h6">{formatDate(new Date(), 'MM/dd/yyyy')}</Typography>
                        </Stack>
                    </Stack>
                    <Stack direction={'row'} spacing={1} alignItems={'flex-start'} justifyContent={'space-between'}>
                        {/* Member Signature */}
                        <Box>
                            <Signature
                                isSpanish={true}
                                buttonLabel={'Firma del paciente'}
                                isOpen={openMemberSignatureOne}
                                setIsOpen={setOpenMemberSignatureOne}
                                signature={digitalSignatureForm.MemberSignatureOne}
                                setSignature={(signature: string) =>
                                    dispatch(
                                        setDigitalSignatureForm({
                                            ...digitalSignatureForm,
                                            MemberSignatureOne: signature,
                                        })
                                    )
                                }
                                notRequired={true}
                            />
                        </Box>
                        {/* Guardian Signature */}
                        <Stack>
                            <Signature
                                isSpanish={true}
                                buttonLabel={
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <Typography variant="button">Firma del padre/de la madre/del tutor</Typography>
                                        <Typography variant="caption" display="block">
                                            (Si el paciente es un niño/una niña menor de 18 años)
                                        </Typography>
                                    </Box>
                                }
                                isOpen={openGuardian}
                                setIsOpen={setOpenGuardian}
                                signature={digitalSignatureForm.GuardianOrCaregiverSignatureOne}
                                setSignature={(signature: string) =>
                                    dispatch(
                                        setDigitalSignatureForm({
                                            ...digitalSignatureForm,
                                            GuardianOrCaregiverSignatureOne: signature,
                                        })
                                    )
                                }
                                notRequired={true}
                            />
                            <Typography variant="body1" fontStyle={'italic'} textAlign={'center'}>
                                * Tanto el niño/la niña como el padre/la madre/el tutor deben aceptar divulgar la información sobre trastornos por consumo de
                                sustancias del niño/de la niña, a menos que este/a esté casado/a, no tenga hogar o se haya emancipado.
                            </Typography>
                        </Stack>
                        <Stack>
                            {/* Decision Maker */}
                            <Signature
                                isSpanish={true}
                                buttonLabel={'Firma de la persona que toma las decisiones en cuanto a la atención médica del paciente'}
                                isOpen={openDecisionMaker}
                                setIsOpen={setOpenDecisionMaker}
                                signature={digitalSignatureForm.MemberSignatureThree}
                                setSignature={(signature: string) =>
                                    dispatch(
                                        setDigitalSignatureForm({
                                            ...digitalSignatureForm,
                                            MemberSignatureThree: signature,
                                        })
                                    )
                                }
                                notRequired={true}
                            />
                            <Typography variant="body1" fontStyle={'italic'} textAlign={'center'}>
                                (Si un tribunal declaró al paciente como incompetente o si este falleció)
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack sx={{ mt: 5 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalSignatureForm.VerbalConsent}
                                    onChange={() =>
                                        dispatch(
                                            setDigitalSignatureForm({
                                                ...digitalSignatureForm,
                                                VerbalConsent: !digitalSignatureForm.VerbalConsent,
                                            })
                                        )
                                    }
                                />
                            }
                            label={
                                <Typography>
                                    Consentimiento verbal es requerido si el miembro o representante legal es incapaz físicamente de firmar al momento y el
                                    consentimiento de cuidados ha sido explícitamente otorgado.
                                    <b> El consentimiento verbal debe ser atestiguado por dos personas telefónicamente o en persona.</b>
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalSignatureForm.ReviewedOne}
                                    onChange={() =>
                                        dispatch(
                                            setDigitalSignatureForm({
                                                ...digitalSignatureForm,
                                                ReviewedOne: !digitalSignatureForm.ReviewedOne,
                                            })
                                        )
                                    }
                                />
                            }
                            label={
                                <Typography>
                                    <b> El proveedor ha revisado esta página del formulario de consentimiento con el miembro</b>
                                </Typography>
                            }
                        />
                        {digitalSignatureForm.VerbalConsent && (
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        helperText="Firma Digital del Testigo"
                                        sx={{ mt: 5 }}
                                        fullWidth
                                        label={'Testigo Uno'}
                                        value={digitalSignatureForm.VerbalConsentWitnessOne}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentWitnessOne: e.target.value,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        helperText="Firma Digital del Testigo"
                                        sx={{ mt: 5 }}
                                        fullWidth
                                        label={'Testigo Dos'}
                                        value={digitalSignatureForm.VerbalConsentWitnessTwo}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentWitnessTwo: e.target.value,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <Signature
                                        isSpanish={true}
                                        buttonLabel={'Firma del Testigo de Consentimiento Verbal'}
                                        isOpen={openVerbalConsent}
                                        setIsOpen={setOpenVerbalConsent}
                                        signature={digitalSignatureForm.VerbalConsentSignature}
                                        setSignature={(signature: string) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentSignature: signature,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Stack>
                    <Typography variant="body1" fontWeight={'bold'} textAlign={'center'} mt={5}>
                        Notificación para el destinatario de la información sobre trastornos por consumo de sustancias: El 42 CFR parte 2 prohíbe la divulgación
                        no autorizada de estos registros.
                    </Typography>
                    <DialogActions>
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={2} p={2} sx={{ width: '100%' }}>
                            <Button
                                disabled={activeStep === 0}
                                onClick={() => {
                                    dispatch(setActiveStep(activeStep - 1));
                                }}
                                sx={{ minWidth: '120px' }}
                                variant="contained"
                            >
                                Anterior
                            </Button>
                            <Button variant="outlined" sx={{ minWidth: '120px' }} onClick={() => dispatch(setOpenSignConsentTwoSpanish(false))}>
                                Cancelar
                            </Button>
                            <Button onClick={handleSubmitConsentPartTwo} sx={{ minWidth: '120px' }} variant="contained" disabled={isSaving}>
                                {activeStep < 2 ? 'Siguiente' : 'Guardar y Finalizar'}
                            </Button>
                        </Stack>
                    </DialogActions>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ConsentPartTwoSpanish;
